/**
 * OperatorPills — color-coded six-state toggle: < ≤ = ≠ > ≥
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import type { RuleOperator } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

export type OpGroup = 'lt' | 'eq' | 'neq' | 'gt';

export const OP_META: Record<RuleOperator, { label: string; group: OpGroup; phrase: string }> = {
  '<':  { label: '<',  group: 'lt',  phrase: 'is less than' },
  '<=': { label: '≤', group: 'lt',  phrase: 'is at most' },
  '=':  { label: '=',  group: 'eq',  phrase: 'equals' },
  '!=': { label: '≠', group: 'neq', phrase: 'is not' },
  '>':  { label: '>',  group: 'gt',  phrase: 'is greater than' },
  '>=': { label: '≥', group: 'gt',  phrase: 'is at least' },
};

const OP_STYLES: Record<OpGroup, { base: string; active: string }> = {
  lt:  { base: 'border-sky-400/40 text-sky-500 bg-sky-500/5',           active: 'bg-sky-500 border-sky-500 text-white' },
  eq:  { base: 'border-emerald-400/40 text-emerald-500 bg-emerald-500/5', active: 'bg-emerald-500 border-emerald-500 text-white' },
  neq: { base: 'border-destructive/40 text-destructive bg-destructive/5',  active: 'bg-destructive border-destructive text-destructive-foreground' },
  gt:  { base: 'border-amber-400/40 text-amber-500 bg-amber-500/5',      active: 'bg-amber-500 border-amber-500 text-white' },
};

const OPERATORS = Object.keys(OP_META) as RuleOperator[];

export interface OperatorPillsProps {
  current: RuleOperator;
  onChange: (op: RuleOperator) => void;
}

const OperatorPills: FC<OperatorPillsProps> = ({ current, onChange }) => (
  <div
    role="radiogroup"
    aria-label="Comparison operator"
    className="flex items-center gap-px rounded border border-border bg-muted/20 p-px"
  >
    {OPERATORS.map((op) => {
      const { label, group, phrase } = OP_META[op];
      const { base, active } = OP_STYLES[group];
      const isActive = current === op;
      return (
        <button
          key={op}
          type="button"
          role="radio"
          aria-checked={isActive}
          aria-label={phrase}
          onClick={() => onChange(op)}
          className={cn(
            'h-7 min-w-[1.75rem] rounded-sm border px-1.5 text-[11px] font-bold transition-colors',
            isActive ? active : base,
          )}
        >
          {label}
        </button>
      );
    })}
  </div>
);

export default OperatorPills;
